import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Phone, Calendar, Users, DollarSign, RefreshCw, Pencil, Check, History } from "lucide-react";
import { api } from "../../lib/api";

/**
 * Compact per-founder + together roll-up card that lives in the admin sidebar.
 * Auto-refreshes every 45s while visible.
 */
export default function TeamPerformanceCard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);   // {name, field}
  const [editVal, setEditVal] = useState("");
  const [showHistory, setShowHistory] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/admin/team-stats")
      .then(({ data }) => setStats(data))
      .catch(() => toast.error("Could not load team stats."))
      .finally(() => setLoading(false));
  };
  useEffect(() => {
    load();
    const tick = () => { if (document.visibilityState === "visible" && !editing) load(); };
    const id = setInterval(tick, 45000);
    return () => clearInterval(id);
  }, [editing]);

  const money = (c) => `$${((c || 0) / 100).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
  const isEd = (name, field) => editing && editing.name === name && editing.field === field;

  const startEdit = (name, field, s) => { setEditing({ name, field }); setEditVal(String(Math.round((s?.[`${field}_cents`] || 0) / 100))); };
  const saveEdit = async (name, field) => {
    const dollars = Math.max(0, Math.round(parseFloat(editVal) || 0));
    try {
      await api.put("/admin/team-stats/adjust", { founder: name, field, cents: dollars * 100 });
      toast.success(`${name}'s ${field} updated.`);
      setEditing(null);
      load();
    } catch { toast.error("Could not save that amount."); }
  };

  const editableAmount = (name, field, s, cls) => isEd(name, field) ? (
    <span className="flex items-center gap-1">
      <span className="text-[10px] text-[#4d5a24]">$</span>
      <input autoFocus type="number" min="0" data-testid={`team-${field}-input-${name.toLowerCase()}`}
        value={editVal} onChange={(e) => setEditVal(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter") saveEdit(name, field); if (e.key === "Escape") setEditing(null); }}
        className="w-20 rounded border border-[#c9a961] bg-white px-1.5 py-0.5 text-[10px] text-right tabular-nums focus:outline-none" />
      <button type="button" data-testid={`team-${field}-save-${name.toLowerCase()}`} onClick={() => saveEdit(name, field)}
        className="text-[#4d5a24] hover:text-[#1e3d3a]"><Check className="h-3 w-3" /></button>
    </span>
  ) : (
    <button type="button" data-testid={`team-${field}-edit-${name.toLowerCase()}`} onClick={() => startEdit(name, field, s)}
      title="Click to edit"
      className={`group inline-flex items-center gap-1 text-[10px] font-medium tabular-nums transition-colors duration-200 ${cls}`}>
      {money(s?.[`${field}_cents`])}
      <Pencil className="h-2.5 w-2.5 opacity-0 group-hover:opacity-100 transition-opacity" />
    </button>
  );

  const founderRow = (name, s) => (
    <div key={name} data-testid={`team-founder-${name.toLowerCase()}`} className="py-1.5 border-b border-[#1e3d3a]/6 last:border-b-0">
      <div className="flex items-center justify-between mb-0.5">
        <span className="text-[11px] uppercase tracking-[0.22em] text-[#1e3d3a]/75">{name}</span>
        <span className="inline-flex items-center gap-0.5 text-[9px] uppercase tracking-[0.1em] text-[#1e3d3a]/40">saved {editableAmount(name, "savings", s, "text-[#4d5a24] hover:text-[#a3823f]")}</span>
      </div>
      <div className="flex items-center justify-end mb-1">
        <span className="inline-flex items-center gap-0.5 text-[9px] uppercase tracking-[0.1em] text-[#1e3d3a]/40">revenue {editableAmount(name, "revenue", s, "text-[#a3823f] hover:text-[#1e3d3a]")}</span>
      </div>
      <div className="flex items-center gap-2.5 text-[9.5px] text-[#1e3d3a]/60">
        <span className="inline-flex items-center gap-0.5" title={`${s?.voicemails || 0} voicemails included`}>
          <Phone className="h-2.5 w-2.5" strokeWidth={2} />
          <span className="tabular-nums font-medium">{s?.calls || 0}</span>
        </span>
        <span className="inline-flex items-center gap-0.5" title="Bookings attributed">
          <Calendar className="h-2.5 w-2.5" strokeWidth={2} />
          <span className="tabular-nums font-medium">{s?.bookings || 0}</span>
        </span>
        <span className="inline-flex items-center gap-0.5" title="Clients converted">
          <Users className="h-2.5 w-2.5" strokeWidth={2} />
          <span className="tabular-nums font-medium">{s?.clients || 0}</span>
        </span>
      </div>
    </div>
  );

  return (
    <div data-testid="team-performance" className="rounded-xl border border-[#1e3d3a]/12 bg-white/80 backdrop-blur-sm px-3 py-3">
      <div className="flex items-center justify-between mb-2 px-1">
        <p className="text-[10px] uppercase tracking-[0.24em] text-[#1e3d3a]/55">Team</p>
        <button
          type="button"
          data-testid="team-refresh"
          onClick={load}
          className="text-[#1e3d3a]/45 hover:text-[#a3823f] transition-colors duration-300"
          title="Refresh"
        >
          <RefreshCw className={`h-3 w-3 ${loading ? "animate-spin" : ""}`} />
        </button>
      </div>
      {loading && !stats && (
        <div className="flex items-center gap-1 text-[11px] text-[#1e3d3a]/50 px-1">
          <Loader2 className="h-3 w-3 animate-spin" /> Loading…
        </div>
      )}
      {stats && (
        <>
          <div className="px-1">
            {Object.entries(stats.founders || {}).map(([name, s]) => founderRow(name, s))}
          </div>
          <div className="mt-2 pt-2 border-t border-[#1e3d3a]/10 px-1" data-testid="team-together">
            <div className="flex items-center justify-between gap-2 mb-0.5">
              <span className="text-[11px] uppercase tracking-[0.22em] text-[#c9a961] shrink-0">Together</span>
              <span className="text-xs font-head font-medium text-[#4d5a24] tabular-nums inline-flex items-center gap-0.5 whitespace-nowrap" title="Total saved">
                <DollarSign className="h-3 w-3 shrink-0" strokeWidth={2} />
                {(stats.team?.savings_cents / 100 || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}
              </span>
            </div>
            {(stats.team?.revenue_cents > 0) && (
              <div className="flex items-center justify-end mb-1">
                <span className="text-xs font-head font-medium text-[#a3823f] tabular-nums inline-flex items-center gap-0.5 whitespace-nowrap" title="Total revenue" data-testid="team-revenue-total">
                  <DollarSign className="h-3 w-3 shrink-0" strokeWidth={2} />
                  {(stats.team?.revenue_cents / 100 || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })} rev
                </span>
              </div>
            )}
            <div className="flex items-center gap-2.5 text-[9.5px] text-[#1e3d3a]/60">
              <span className="inline-flex items-center gap-0.5" title="Calls made">
                <Phone className="h-2.5 w-2.5" strokeWidth={2} />
                <span className="tabular-nums font-medium">{stats.team?.calls || 0}</span> calls
              </span>
              <span className="inline-flex items-center gap-0.5">
                <Calendar className="h-2.5 w-2.5" strokeWidth={2} />
                <span className="tabular-nums font-medium">{stats.team?.bookings || 0}</span> booked
              </span>
              <span className="inline-flex items-center gap-0.5">
                <Users className="h-2.5 w-2.5" strokeWidth={2} />
                <span className="tabular-nums font-medium">{stats.team?.clients || 0}</span> clients
              </span>
            </div>
          </div>
          {(stats.edit_log?.length > 0) && (
            <div className="mt-2 pt-2 border-t border-[#1e3d3a]/10 px-1" data-testid="team-history">
              <button type="button" data-testid="team-history-toggle" onClick={() => setShowHistory((v) => !v)}
                className="inline-flex items-center gap-1 text-[9px] uppercase tracking-[0.16em] text-[#1e3d3a]/45 hover:text-[#a3823f] transition-colors">
                <History className="h-2.5 w-2.5" /> Edit history
              </button>
              {showHistory && (
                <ul className="mt-1.5 space-y-1">
                  {stats.edit_log.map((e, i) => (
                    <li key={i} className="text-[9.5px] text-[#1e3d3a]/55 leading-snug">
                      <b className="text-[#1e3d3a]/75">{e.by}</b> set {e.founder}'s {e.field} to {money(e.new_cents)}
                      <span className="text-[#1e3d3a]/35"> · {e.at ? new Date(e.at).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : ""}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
